import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Unsubscriber } from '@core-lib/classes';
import { ModalRef } from '@core-lib/modules/modal-container';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { PreferenceService } from '@core-lib/services/preference.service';
import { IEditPreferences, IPreference, PreferenceContact } from '@core-lib/models/preference.model';
import { Preference } from '@core-lib/enums/preference.enum';

@Component({
    selector: 'settings-contact',
    templateUrl: './settings-contact.component.html',
    styleUrls: ['./settings-contact.component.scss'],
    standalone: false
})
export class SettingsContactComponent extends Unsubscriber implements OnInit, OnDestroy {
	@Input() structureId: string;
	@Input() allowEdit: boolean;
	@Input() preferenceContact$: Observable<PreferenceContact>;

	modalRef: ModalRef;
	form: FormGroup;

	preferenceContact: PreferenceContact | null = null;
	anyEditMode$: Observable<boolean> = this.preferenceService.anyEditMode$;

	public selectedCountryISO: string = 'fr';

	get isEditMode() {
		return this.preferenceService.contactEditMode$.value;
	}

	constructor(
		public preferenceService: PreferenceService,
		protected store$: Store,
		private fb: FormBuilder,
		private toastr: ToastrService,
		private translate: TranslateService,
	) {
		super();
	}

	ngOnInit(): void {

		if (this.preferenceContact$) {
			this.preferenceContact$.pipe(takeUntil(this.destroySubscriptions$)).subscribe(pref => {
				if (pref) {
					this.preferenceContact = pref;
					this.initForm();
				}
			});
		}
	}

	private initForm(): void {
		this.preferenceService.contactEditMode$.next(false);
		this.form = this.fb.group({
			email: [this.preferenceContact.email?.valeur || '', [Validators.email]],
			telephone: [this.preferenceContact.telephone?.valeur || '']
		});
	}

	edit(): void {
		this.preferenceService.contactEditMode$.next(true);
	}

	cancel(): void {
		this.initForm();
	}

	confirm(): void {
		if (!this.allowEdit) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}
		const email = this.form.value.email;
		let telephone = this.form.value.telephone;

		if (telephone && typeof telephone === 'object') {
			telephone = telephone.internationalNumber || telephone.nationalNumber || telephone.number || '';
		}

		const preferences: IPreference[] = [
			{
				actif: true,
				code: Preference.contact.email.code,
				valeur: email,
				utilisateurModification: null,
				dateModification: null
			},
			{
				actif: true,
				code: Preference.contact.telephone.code,
				valeur: telephone,
				utilisateurModification: null,
				dateModification: null
			}
		].filter(p => !!p.valeur);

		const editPreferences: IEditPreferences = {
			category: Preference.category.contact,
			preferences
		};

		this.preferenceService.edit(this.structureId, editPreferences).subscribe({
			next: () => {
				this.translate.get('Success.Save').subscribe(msg => this.toastr.success(msg));
				this.preferenceService.contactEditMode$.next(false);
				this.preferenceService.get(this.structureId, true);
			},
			error: error => {
				this.toastr.error(error);
			}
		});
	}

	displayTelephone(value: string): string {
		if (!value) return '';
		const phoneStr = value.toString().replace(/\s+/g, '');
		return phoneStr.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
	}
}
